import { useState, useMemo } from 'react';
import { useMonitors } from '../../hooks/useMonitors';
import { useDebounce } from '../../hooks/useDebounce';
import { MonitorCard } from '../../components/monitor/MonitorCard';
import { MonitorFilters, DIAGONAL_MIN, DIAGONAL_MAX, REFRESH_MIN } from '../../components/monitor/MonitorFilters';
import type { SidebarFilters } from '../../components/monitor/MonitorFilters';
import type { Monitor, MonitorFilters as MonitorFiltersType, MonitorSortKey } from '../../types/Monitor';
import { SORT_LABELS } from '../../constants';
import styles from './CataloguePage.module.css';

interface CataloguePageProps {
  /** Called when the user clicks "Preview" on a monitor card. */
  onSelectMonitor?: (monitor: Monitor) => void;
}

const EMPTY_SIDEBAR_FILTERS: SidebarFilters = {
  brands: [],
  panelTypes: [],
  diagonalRange: [DIAGONAL_MIN, DIAGONAL_MAX],
  resolutionTiers: [],
  minRefreshRate: REFRESH_MIN,
  minBrightnessNits: 0,
  requiredPorts: [],
  maxPriceUSD: undefined,
  hdrOnly: false,
};

export function CataloguePage({ onSelectMonitor }: CataloguePageProps) {
  const [search, setSearch] = useState('');
  const [sidebarFilters, setSidebarFilters] = useState<SidebarFilters>(EMPTY_SIDEBAR_FILTERS);
  const [sort, setSort] = useState<MonitorSortKey>('relevance');

  const debouncedSearch = useDebounce(search, 400);

  const filters = useMemo<MonitorFiltersType>(() => {
    const [minD, maxD] = sidebarFilters.diagonalRange;
    return {
      search:            debouncedSearch || undefined,
      brands:            sidebarFilters.brands.length ? sidebarFilters.brands : undefined,
      minDiagonal:       minD > DIAGONAL_MIN ? minD : undefined,
      maxDiagonal:       maxD < DIAGONAL_MAX ? maxD : undefined,
      panelTypes:        sidebarFilters.panelTypes.length ? sidebarFilters.panelTypes : undefined,
      resolutionTiers:   sidebarFilters.resolutionTiers.length ? sidebarFilters.resolutionTiers : undefined,
      minRefreshRate:    sidebarFilters.minRefreshRate > REFRESH_MIN ? sidebarFilters.minRefreshRate : undefined,
      minBrightnessNits: sidebarFilters.minBrightnessNits > 0 ? sidebarFilters.minBrightnessNits : undefined,
      requiredPorts:     sidebarFilters.requiredPorts.length ? sidebarFilters.requiredPorts : undefined,
      maxPriceUSD:       sidebarFilters.maxPriceUSD,
      hdrOnly:           sidebarFilters.hdrOnly || undefined,
    };
  }, [debouncedSearch, sidebarFilters]);

  const { monitors, total, loading, error } = useMonitors({ filters, sort });

  function handleOpenPreview(monitor: Monitor) {
    onSelectMonitor?.(monitor);
  }

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1 className={styles.title}>Find Your Monitor</h1>
        <p className={styles.subtitle}>
          Browse {total > 0 ? total : '…'} monitors and preview exactly how they'll look on your desk.
        </p>
      </header>

      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <svg className={styles.searchIcon} viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M10.5 10.5L14 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Search monitors…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search monitors"
          />
        </div>
        <select
          className={styles.sortSelect}
          value={sort}
          onChange={(e) => setSort(e.target.value as MonitorSortKey)}
          aria-label="Sort monitors"
        >
          {(Object.entries(SORT_LABELS) as [MonitorSortKey, string][]).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
      </div>

      <div className={styles.layout}>
        <aside className={styles.filtersSidebar}>
          <MonitorFilters filters={sidebarFilters} onChange={setSidebarFilters} />
        </aside>

        <div className={styles.content}>
          {loading && (
            <div className={styles.stateContainer}>
              <div className={styles.spinner} aria-label="Loading monitors" />
              <p className={styles.stateText}>Loading monitors…</p>
            </div>
          )}

          {error && !loading && (
            <div className={styles.stateContainer}>
              <p className={styles.errorText}>{error}</p>
            </div>
          )}

          {!loading && !error && monitors.length === 0 && (
            <div className={styles.stateContainer}>
              <p className={styles.stateText}>No monitors match your filters.</p>
            </div>
          )}

          {!loading && !error && monitors.length > 0 && (
            <ul className={styles.grid}>
              {monitors.map((monitor) => (
                <li key={monitor.id}>
                  <MonitorCard monitor={monitor} onOpenPreview={handleOpenPreview} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
